/**
 * Product Upsell Bundle Component
 * Set vs Pendant Only selector with product style selection and variant picker
 */

if (!customElements.get('product-upsell-bundle')) {
  class ProductUpsellBundle extends HTMLElement {
    constructor() {
      super();
      this.mainProductFormId = this.dataset.mainProductForm;
      this.mainProductColor = this.dataset.mainProductColor;

      // Price data
      this.pendantPrice = parseInt(this.dataset.pendantPrice, 10) || 0;
      this.chainPrice = parseInt(this.dataset.chainPrice, 10) || 0;
      this.bundleDiscount = parseInt(this.dataset.bundleDiscount, 10) || 0;

      // Elements
      this.masterToggle = this.querySelector('[data-upsell-master-toggle]');
      this.content = this.querySelector('[data-upsell-bundle-content]');
      this.productRadios = this.querySelectorAll('[data-upsell-product-radio]');
      this.productCards = this.querySelectorAll('[data-upsell-card]');
      this.variantSelectors = this.querySelectorAll('[data-upsell-variant-selector]');
      this.choiceRadios = this.querySelectorAll('[data-upsell-choice]');
      this.setPriceDisplay = this.querySelector('[data-set-price]');
      this.pendantPriceDisplay = this.querySelector('[data-pendant-price]');

      // State
      this.isEnabled = true; // Default to Complete Set selected
      this.selectedProductId = null;
      this.selectedVariantId = null;
      this.upsellAdded = false;
    }

    connectedCallback() {
      this.init();

      // Handle back-forward cache: browser restores page without re-running init
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          this.syncChoiceState();
        }
      });
    }

    init() {
      this.bindChoiceSelector();
      this.bindProductSelection();
      this.bindSizeSelection();
      this.bindMoreStyles();
      this.bindFitGuide();
      this.interceptFormSubmission();
      this.syncChoiceState();
    }

    syncChoiceState() {
      // Always reset to Complete Set on page load/back navigation
      const setRadio = this.querySelector('[data-upsell-choice="set"]');
      const pendantRadio = this.querySelector('[data-upsell-choice="pendant"]');
      if (setRadio) setRadio.checked = true;
      if (pendantRadio) pendantRadio.checked = false;
      this.isEnabled = true;
      if (this.masterToggle) this.masterToggle.checked = true;
      if (this.content) this.content.style.display = 'block';
      this.selectFirstProduct();
    }

    bindChoiceSelector() {
      this.choiceRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          const choice = radio.dataset.upsellChoice;
          this.isEnabled = choice === 'set';

          // Sync the hidden master toggle for compatibility
          if (this.masterToggle) {
            this.masterToggle.checked = this.isEnabled;
          }

          if (this.content) {
            this.content.style.display = this.isEnabled ? 'block' : 'none';
          }

          if (this.isEnabled) {
            this.selectFirstProduct();
          } else {
            this.resetSelections();
          }

          this.upsellAdded = false;
        });
      });
    }

    bindFitGuide() {
      const overlay = this.querySelector('[data-chain-fit-guide-overlay]');
      if (!overlay) return;

      // Open buttons (multiple, one per variant selector)
      this.querySelectorAll('[data-open-chain-fit-guide]').forEach((btn) => {
        btn.addEventListener('click', () => {
          overlay.style.display = 'flex';
          overlay.scrollTop = 0;
          document.body.style.overflow = 'hidden';
        });
      });

      // Close button
      const closeBtn = overlay.querySelector('[data-close-chain-fit-guide]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          overlay.style.display = 'none';
          document.body.style.overflow = '';
        });
      }

      // Close on overlay click (outside modal)
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          overlay.style.display = 'none';
          document.body.style.overflow = '';
        }
      });

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && overlay.style.display === 'flex') {
          overlay.style.display = 'none';
          document.body.style.overflow = '';
        }
      });
    }

    bindMoreStyles() {
      const moreBtn = this.querySelector('[data-upsell-more-styles]');
      const productsGrid = this.querySelector('[data-upsell-product-options]');
      if (!moreBtn || !productsGrid) return;

      // Hide button if 4 or fewer cards
      if (this.productCards.length <= 4) {
        moreBtn.classList.add('is-hidden');
        return;
      }

      moreBtn.addEventListener('click', () => {
        const isExpanded = productsGrid.classList.toggle('is-expanded');
        moreBtn.classList.toggle('is-expanded', isExpanded);
        moreBtn.querySelector('.upsell-bundle__more-styles-text').textContent =
          isExpanded ? 'Less Chain Styles' : 'More Chain Styles';
      });
    }

    selectFirstProduct() {
      const firstRadio = this.productRadios[0];
      if (firstRadio) {
        firstRadio.checked = true;
        this.handleProductSelection(firstRadio);
        this.preselectDefaultSize(firstRadio.dataset.productId);
      }
    }

    preselectDefaultSize(productId) {
      const selector = this.querySelector(
        `[data-upsell-variant-selector][data-product-id="${productId}"]`
      );
      if (!selector) return;

      // Look for 22" size option first, then fall back to first available
      const sizeOptions = selector.querySelectorAll('[data-upsell-size-option]:not(:disabled)');
      let targetOption = null;

      sizeOptions.forEach((option) => {
        const displayValue = option.dataset.displayValue || '';
        if (displayValue.includes('22')) {
          targetOption = option;
        }
      });

      // Fall back to first available if 22" not found
      if (!targetOption && sizeOptions.length > 0) {
        targetOption = sizeOptions[0];
      }

      if (targetOption) {
        targetOption.checked = true;
        this.handleSizeSelection(selector, targetOption);
      }
    }

    bindProductSelection() {
      this.productRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          this.handleProductSelection(radio);
        });
      });
    }

    handleProductSelection(radio) {
      const productId = radio.dataset.productId;

      this.selectedProductId = productId;
      this.selectedVariantId = null;
      this.upsellAdded = false;

      // Remove selected state from all cards
      this.productCards.forEach((card) => {
        card.classList.remove('is-selected');
      });

      // Add selected state to clicked card
      const selectedCard = this.querySelector(`[data-upsell-card][data-product-id="${productId}"]`);
      if (selectedCard) {
        selectedCard.classList.add('is-selected');
      }

      // Update chain name display in heading
      const chainNameDisplay = this.querySelector('[data-selected-chain-name]');
      if (chainNameDisplay) {
        const chainName = radio.dataset.chainName || '';
        chainNameDisplay.textContent = chainName;
      }

      // Hide all variant selectors and reset their state
      this.variantSelectors.forEach((selector) => {
        selector.style.display = 'none';

        // Reset size selections
        const sizeRadios = selector.querySelectorAll('[data-upsell-size-option]');
        sizeRadios.forEach((sizeRadio) => {
          sizeRadio.checked = false;
        });

        // Clear selected size display
        const selectedDisplay = selector.querySelector('[data-selected-size-value]');
        if (selectedDisplay) {
          selectedDisplay.textContent = '';
        }

        // Clear variant ID (keep auto variant if exists)
        const hiddenInput = selector.querySelector('[data-upsell-variant-id]');
        if (hiddenInput) {
          const autoVariant = selector.querySelector('[data-upsell-auto-variant]');
          hiddenInput.value = autoVariant ? autoVariant.value : '';
        }

        // Clear errors
        const errorMsg = selector.querySelector('[data-upsell-size-error]');
        if (errorMsg) {
          errorMsg.style.display = 'none';
        }
        const fieldset = selector.querySelector('[data-upsell-fieldset]');
        if (fieldset) {
          fieldset.classList.remove('variant-error');
        }
      });

      // Show variant selector for selected product
      const selectedVariantSelector = this.querySelector(
        `[data-upsell-variant-selector][data-product-id="${productId}"]`
      );
      if (selectedVariantSelector) {
        selectedVariantSelector.style.display = 'block';

        // If product has auto variant (no size option), set variant ID
        const autoVariant = selectedVariantSelector.querySelector('[data-upsell-auto-variant]');
        if (autoVariant) {
          this.selectedVariantId = autoVariant.value;
        }
      }

      // Auto-select 22" for every chain
      this.preselectDefaultSize(productId);
    }

    bindSizeSelection() {
      this.variantSelectors.forEach((selector) => {
        const sizeOptions = selector.querySelectorAll('[data-upsell-size-option]');

        sizeOptions.forEach((radio) => {
          radio.addEventListener('change', () => {
            this.handleSizeSelection(selector, radio);
          });
        });
      });
    }

    handleSizeSelection(selector, radio) {
      const productId = selector.dataset.productId;
      const variantId = radio.dataset.variantId;
      const displayValue = radio.dataset.displayValue;

      // Reset upsell flag when size changes
      this.upsellAdded = false;

      // Update hidden input
      const hiddenInput = selector.querySelector('[data-upsell-variant-id]');
      if (hiddenInput) {
        hiddenInput.value = variantId;
      }

      // Update selected size display in legend
      const selectedDisplay = selector.querySelector('[data-selected-size-value]');
      if (selectedDisplay) {
        selectedDisplay.textContent = displayValue;
      }

      // Show "Most Popular" badge only when 22" is selected
      const popularBadge = selector.querySelector('[data-popular-badge]');
      if (popularBadge) {
        popularBadge.style.display = displayValue && displayValue.includes('22') ? '' : 'none';
      }

      // Store selected variant
      if (this.selectedProductId === productId) {
        this.selectedVariantId = variantId;
      }

      // Hide error message
      const errorMsg = selector.querySelector('[data-upsell-size-error]');
      if (errorMsg) {
        errorMsg.style.display = 'none';
      }

      const fieldset = selector.querySelector('[data-upsell-fieldset]');
      if (fieldset) {
        fieldset.classList.remove('variant-error');
      }
    }

    resetSelections() {
      this.selectedProductId = null;
      this.selectedVariantId = null;

      // Uncheck all product radios
      this.productRadios.forEach((radio) => {
        radio.checked = false;
      });

      // Remove selected state from all cards
      this.productCards.forEach((card) => {
        card.classList.remove('is-selected');
      });

      // Hide all variant selectors and reset
      this.variantSelectors.forEach((selector) => {
        selector.style.display = 'none';

        const sizeRadios = selector.querySelectorAll('[data-upsell-size-option]');
        sizeRadios.forEach((sizeRadio) => {
          sizeRadio.checked = false;
        });

        // Clear selected size display
        const selectedDisplay = selector.querySelector('[data-selected-size-value]');
        if (selectedDisplay) {
          selectedDisplay.textContent = '';
        }

        // Hide popular badge
        const popularBadge = selector.querySelector('[data-popular-badge]');
        if (popularBadge) {
          popularBadge.style.display = 'none';
        }

        const hiddenInput = selector.querySelector('[data-upsell-variant-id]');
        if (hiddenInput) {
          const autoVariant = selector.querySelector('[data-upsell-auto-variant]');
          hiddenInput.value = autoVariant ? autoVariant.value : '';
        }

        const errorMsg = selector.querySelector('[data-upsell-size-error]');
        if (errorMsg) {
          errorMsg.style.display = 'none';
        }

        const fieldset = selector.querySelector('[data-upsell-fieldset]');
        if (fieldset) {
          fieldset.classList.remove('variant-error');
        }
      });
    }

    interceptFormSubmission() {
      // Find the main product form
      this.mainForm = document.getElementById(this.mainProductFormId);

      // Handle main form add-to-cart button clicks
      document.addEventListener(
        'click',
        async (event) => {
          const button = event.target.closest('[data-add-to-cart], [type="submit"][name="add"]');
          if (!button) return;

          const form = button.form || button.closest('form');
          if (!form) return;

          // Check if this is the main product form
          const formId = form.getAttribute('id');

          if (formId !== this.mainProductFormId) return;

          // If upsell not enabled, allow normal submission
          if (!this.isEnabled) return;

          // If no product selected, allow normal submission
          if (!this.selectedProductId) return;

          // Validate size selection
          if (!this.validateSelection()) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return false;
          }

          // If we already added the upsell, let the form submit normally
          if (this.upsellAdded) {
            this.upsellAdded = false;
            return;
          }

          // Prevent default form submission
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          // Immediately show loading state on button
          const originalDisabled = button.disabled;
          button.disabled = true;
          button.classList.add('is-loading');
          button.setAttribute('aria-busy', 'true');

          try {
            // Add upsell product to cart first
            await this.addUpsellToCart();

            // Longer delay to let cart state sync and avoid rate limiting
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Set flag so we skip upsell logic on re-click
            this.upsellAdded = true;

            // Restore button state before re-clicking
            button.disabled = originalDisabled;
            button.classList.remove('is-loading');
            button.setAttribute('aria-busy', 'false');

            // Create and dispatch a new click event that mimics user interaction
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
            });
            button.dispatchEvent(clickEvent);
          } catch (error) {
            // Restore button state on error
            button.disabled = originalDisabled;
            button.classList.remove('is-loading');
            button.setAttribute('aria-busy', 'false');
            console.error('Error in upsell flow:', error);
          }
        },
        true
      );

      // Handle sticky cart-bar add-to-cart button clicks
      document.addEventListener(
        'click',
        async (event) => {
          const cartBarButton = event.target.closest('[data-cart-bar-add-to-cart]');
          if (!cartBarButton) return;

          // If upsell not enabled, allow normal cart-bar flow
          if (!this.isEnabled) return;

          // If no product selected, allow normal cart-bar flow
          if (!this.selectedProductId) return;

          // Validate size selection
          if (!this.validateSelection()) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return false;
          }

          // If we already added the upsell, let the cart-bar flow continue normally
          if (this.upsellAdded) {
            return;
          }

          // Prevent cart-bar's default handling
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          // Show loading state on cart-bar button
          const originalDisabled = cartBarButton.disabled;
          cartBarButton.disabled = true;
          cartBarButton.classList.add('is-loading');
          cartBarButton.setAttribute('aria-busy', 'true');

          try {
            // Add upsell product to cart first
            await this.addUpsellToCart();

            // Longer delay to let cart state sync and avoid rate limiting
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Set flag so we skip upsell logic on re-click
            this.upsellAdded = true;

            // Restore button state before re-clicking
            cartBarButton.disabled = originalDisabled;
            cartBarButton.classList.remove('is-loading');
            cartBarButton.setAttribute('aria-busy', 'false');

            // Re-dispatch click on cart-bar button to trigger normal cart-bar flow
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
            });
            cartBarButton.dispatchEvent(clickEvent);
          } catch (error) {
            // Restore button state on error
            cartBarButton.disabled = originalDisabled;
            cartBarButton.classList.remove('is-loading');
            cartBarButton.setAttribute('aria-busy', 'false');
            console.error('Error in upsell flow (cart-bar):', error);
          }
        },
        true
      );
    }

    validateSelection() {
      const selectedVariantSelector = this.querySelector(
        `[data-upsell-variant-selector][data-product-id="${this.selectedProductId}"]`
      );

      if (!selectedVariantSelector) return true;

      // Check if product has auto variant (no size options)
      const autoVariant = selectedVariantSelector.querySelector('[data-upsell-auto-variant]');

      // If auto variant exists (no size options), validation passes
      if (autoVariant) {
        this.selectedVariantId = autoVariant.value;
        return true;
      }

      // Check if a size is selected
      const hiddenInput = selectedVariantSelector.querySelector('[data-upsell-variant-id]');

      if (!hiddenInput || !hiddenInput.value) {
        // Show error
        const errorMsg = selectedVariantSelector.querySelector('[data-upsell-size-error]');
        const fieldset = selectedVariantSelector.querySelector('[data-upsell-fieldset]');

        if (errorMsg) {
          errorMsg.style.display = 'block';
        }

        if (fieldset) {
          fieldset.classList.add('variant-error');
        }

        // Hide header during validation scroll (mobile)
        const header = document.querySelector('[data-header-sticky]');
        if (header) {
          header.classList.add('header-scroll-hide');
          header.dataset.lockedByValidation = 'true';
        }

        // Scroll to error
        selectedVariantSelector.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Keep header hidden during smooth scroll
        setTimeout(() => {
          if (header && header.dataset.lockedByValidation === 'true') {
            header.classList.add('header-scroll-hide');
          }
        }, 300);

        return false;
      }

      this.selectedVariantId = hiddenInput.value;
      return true;
    }

    async addUpsellToCart(retryCount = 0) {
      if (!this.selectedVariantId) {
        console.warn('No variant ID selected for upsell');
        return false;
      }

      const variantId = parseInt(this.selectedVariantId, 10);
      if (isNaN(variantId)) {
        console.error('Invalid variant ID:', this.selectedVariantId);
        return false;
      }

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: {
                  _bundle_set: 'pendant-chain',
                },
              },
            ],
          }),
        });

        // Handle rate limiting (429)
        if (response.status === 429) {
          if (retryCount < 2) {
            // Wait and retry
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return this.addUpsellToCart(retryCount + 1);
          }
          console.error('Rate limited by Shopify, please try again');
          return false;
        }

        // Check content type before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.error('Unexpected response type:', contentType);
          return false;
        }

        const data = await response.json();

        if (!response.ok) {
          console.error(
            'Failed to add upsell item:',
            data.message || data.description || 'Unknown error'
          );
          return false;
        }

        return true;
      } catch (error) {
        console.error('Error adding upsell item:', error);
        return false;
      }
    }

    formatMoney(cents) {
      let formatted;
      if (typeof window.theme !== 'undefined' && typeof window.theme.formatMoney === 'function') {
        formatted = window.theme.formatMoney(cents, window.theme.moneyFormat);
      } else {
        // Fallback formatting
        const amount = (cents / 100).toFixed(2);
        formatted = '$' + amount;
      }

      // Strip HTML tags that money formatters may include
      const temp = document.createElement('div');
      temp.innerHTML = formatted;
      return temp.textContent || temp.innerText || formatted;
    }
  }

  customElements.define('product-upsell-bundle', ProductUpsellBundle);
}
